/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Drawer,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  addEdge,
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useNodesInitialized,
  useReactFlow,
  Handle,
  Position,
} from '@xyflow/react';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import TransformIcon from '@mui/icons-material/Transform';
import VectorizeIcon from '@mui/icons-material/LinearScale';
import DecisionIcon from '@mui/icons-material/Assessment';
import ContentIcon from '@mui/icons-material/Description';
import GenerateIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ViewListIcon from '@mui/icons-material/ViewList';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import axios from 'axios';
import { environment } from '../../environments/environment';
import { useNavigate, useLocation } from 'react-router-dom';
import { ButtonK } from '@datascient/datascient-ui-library';
import ChipInput from 'datascient-ui-library/src/lib/Molecules/MultiInputField/MultiInput';
import dagre from 'dagre';
import AdminActivityTemplates from './AdminActivityTemplates';
import axiosInstance from '../api';
import ChatAssistance from './ChatAssistance';
import { ScopeBadge, ModeLabelChip, getNodeGlyph, getInteractionMode } from './flowNodeVisuals';
import IntegrationBindingCard from './IntegrationBindingCard';
import IntegrationExplorerDrawer from './IntegrationExplorerDrawer';
import TaskConfigurationForm from './TaskConfigurationForm';
import { dsVar, ink } from '../theme/tokens';

const nodeWidth = 280;
const nodeHeight = 80;

// Task type configurations
const taskTypeConfig = {
  'CONTENT_RETRIEVER': {
    icon: <ContentIcon />,
    color: '#4CAF50',
    bgColor: 'rgba(76, 175, 80, 0.1)',
  },
  'INPUT_PROCESSOR': {
    icon: <InputIcon />,
    color: '#2196F3',
    bgColor: 'rgba(33, 150, 243, 0.1)',
  },
  'OUTPUT_PROCESSOR': {
    icon: <OutputIcon />,
    color: '#FF9800',
    bgColor: 'rgba(255, 152, 0, 0.1)',
  },
  'FEATURE_EXTRACTOR': {
    icon: <TransformIcon />,
    color: '#9C27B0',
    bgColor: 'rgba(156, 39, 176, 0.1)',
  },
  'FEATURE_VECTORIZER': {
    icon: <VectorizeIcon />,
    color: '#E91E63',
    bgColor: 'rgba(233, 30, 99, 0.1)',
  },
  'DATA_TRANSFORMER': {
    icon: <TransformIcon />,
    color: '#00BCD4',
    bgColor: 'rgba(0, 188, 212, 0.1)',
  },
  'DECISION_AGENT': {
    icon: <DecisionIcon />,
    color: '#F44336',
    bgColor: 'rgba(244, 67, 54, 0.1)',
  },
  'CONTENT_GENERATOR': {
    icon: <GenerateIcon />,
    color: '#673AB7',
    bgColor: 'rgba(103, 58, 183, 0.1)',
  },
  'Uncategorized': {
    icon: <FolderIcon />,
    color: dsVar('fg-muted'),
    bgColor: 'rgba(96, 125, 139, 0.1)',
  },
};

const toTitleCase = (str) => {
  if (!str) return 'Uncategorized';
  return str.toLowerCase().split(/[_\s]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};



const nodeStyles = `
  .react-flow__node {
    border-radius: 4px;
    padding: 0;
    background: transparent;
    border: none;
    color: rgb(var(--ds-fg-body));
    transition: all 0.2s ease-in-out;
  }
  .react-flow__node:hover {
    border-color: rgb(var(--ds-accent-fg));
    box-shadow: 0 0 15px rgba(255, 222, 89, 0.2);
    transform: translateY(-2px);
  }
  .react-flow__handle {
    width: 14px;
    height: 14px;
    background: rgb(var(--ds-accent));
    border: 3px solid rgb(var(--ds-page));
    box-shadow: 0 0 6px rgba(255, 222, 89, 0.4);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .react-flow__handle:hover {
    width: 20px;
    height: 20px;
    background: #FFF;
    box-shadow: 0 0 12px rgba(255, 222, 89, 0.9);
  }
  .react-flow__edge-path {
    stroke: rgb(var(--ds-accent-fg));
    stroke-width: 2.5;
    filter: drop-shadow(0 0 3px rgba(255, 222, 89, 0.4));
  }
  /* Dark gold on white needs its full strength; the dimmed edges read on navy only. */
  [data-theme="light"] .react-flow__edge-path {
    opacity: 1 !important;
  }
  .react-flow__panel.react-flow__minimap {
    margin: 0 !important;
    top: auto !important;
    left: auto !important;
    right: 0 !important;
    bottom: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-top-right-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    transform: none !important;
  }
  .react-flow__controls {
    background: rgb(var(--ds-surface));
    border: 1px solid rgb(var(--ds-line));
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
  .react-flow__controls-button {
    background: rgb(var(--ds-surface));
    color: rgb(var(--ds-fg-body));
    border-bottom: 1px solid rgb(var(--ds-line));
    fill: currentColor;
  }
  /* Read-only drawer: keep text legible when fields are disabled */
  .MuiInputBase-input.Mui-disabled {
    -webkit-text-fill-color: rgb(var(--ds-fg-soft)) !important;
    opacity: 1 !important;
  }
  .MuiInputBase-root.Mui-disabled {
    background-color: rgb(var(--ds-page)) !important;
  }
  /* ── Typography: JetBrains Mono for headings, Inter for body ── */
  .MuiTypography-root {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiTypography-h1,
  .MuiTypography-h2,
  .MuiTypography-h3,
  .MuiTypography-h4,
  .MuiTypography-h5,
  .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Catalog & panel section labels (uppercase caps labels rendered as body) */
  .task-catalog-header,
  .MuiAccordionSummary-content .MuiTypography-root {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Drawer & page title h6 overrides */
  .MuiDrawer-paper .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  /* Input text stays Inter */
  .MuiInputBase-root,
  .MuiInputBase-input {
    font-family: 'Inter', sans-serif !important;
  }
  /* ── Drawer-specific (portal-rendered) font guarantees ── */
  .MuiDrawer-paper .MuiTypography-root {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiDrawer-paper .MuiTypography-h4,
  .MuiDrawer-paper .MuiTypography-h5,
  .MuiDrawer-paper .MuiTypography-h6 {
    font-family: 'JetBrains Mono', monospace !important;
  }
  .MuiDrawer-paper .MuiInputBase-root,
  .MuiDrawer-paper .MuiInputBase-input,
  .MuiDrawer-paper .MuiSelect-select {
    font-family: 'Inter', sans-serif !important;
  }
  .MuiDrawer-paper .MuiTableCell-root {
    font-family: 'Inter', sans-serif !important;
  }
  /* Dropdown menu list items */
  .MuiPopover-root .MuiMenuItem-root {
    font-family: 'Inter', sans-serif !important;
  }
  /* ── ReactFlow canvas: node & edge label fonts ── */
  .react-flow__node,
  .react-flow__node * {
    font-family: 'Inter', sans-serif !important;
  }
  .react-flow__edge-textwrapper,
  .react-flow__edge-label {
    font-family: 'Inter', sans-serif !important;
  }
`;

const CustomFlowNode = ({ data }) => {
  const config = taskTypeConfig[data.activity_type] || taskTypeConfig[data.activityType] || taskTypeConfig['Uncategorized'];
  const isSubFlow = data.linked_flow_template_code && data.linked_flow_template_code !== '';
  // Human-control posture (interaction_mode) drives the node's border, left
  // rail colour and mode chip. All nodes keep the same card shape; gateway modes
  // (APPROVAL_REQUIRED / HUMAN_ONLY) are distinguished by their coral/navy
  // accent, mode chip and a gate icon in the main icon slot. Sub-flow styling
  // takes precedence.
  const im = getInteractionMode(data.interaction_mode);
  const NodeGlyph = getNodeGlyph(data.interaction_mode, DirectionsRunIcon);
  return (
    <Box sx={{
      padding: 0,
      borderRadius: '4px',
      background: 'transparent',
      border: isSubFlow ? '1px dashed #7C3AED' : `${im.weight} ${im.border} ${im.stroke}`,
      overflow: 'visible',
      transition: 'all 0.2s ease-in-out',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      '&:hover': {
        transform: 'translateY(-2px)',
        zIndex: 100,
        borderColor: isSubFlow ? '#A78BFA' : im.stroke,
        boxShadow: '0 4px 20px rgba(0,0,0,0.28)',
      }
    }}>
      <Box sx={{
        padding: '14px 16px',
        borderRadius: 0,
        background: dsVar('raised'),
        borderLeft: isSubFlow ? '4px solid #7C3AED' : `4px solid ${im.fill}`,
        width: nodeWidth - 2,
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} />
        <ModeLabelChip mode={data.interaction_mode} />
        <ScopeBadge scope={data.scope} />
        <Box sx={{
          color: isSubFlow ? ink('#A78BFA') : config.color,
          backgroundColor: isSubFlow ? 'rgba(124, 58, 237, 0.15)' : config.bgColor,
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {isSubFlow ? <AccountTreeIcon /> : <NodeGlyph />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '12.5px',
            color: dsVar('fg'),
            lineHeight: 1.4,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontFamily: "'Inter', sans-serif",
          }}>
            {data.activity_template_name || data.activityTemplateName}
          </Typography>
        </Box>
        {isSubFlow && (
          <Chip label="Sub-flow" size="small" sx={{
            position: 'absolute',
            bottom: -10,
            right: 12,
            height: '18px',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            backgroundColor: 'rgba(124, 58, 237, 0.2)',
            color: ink('#A78BFA'),
            border: '1px solid rgba(124, 58, 237, 0.4)',
            borderRadius: '4px',
            '& .MuiChip-label': { px: '6px' },
          }} />
        )}
        <Handle type="source" position={Position.Right} />
      </Box>
    </Box>
  );
};

const nodeTypes = { workflowNode: CustomFlowNode };

// Refits the viewport whenever a new flow is loaded. Required for drill-down:
// navigating to /AIStudio/customize with a different flow_template_code reloads
// the nodes without remounting, so the <ReactFlow fitView /> prop (which only
// runs on initial mount) never re-centers the sub-flow. We wait for the new
// nodes to be measured (useNodesInitialized) before fitting, once per flow.
const FitViewOnFlowChange = ({ flowKey }) => {
  const { fitView } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const fittedKeyRef = useRef(null);

  useEffect(() => {
    // Fit once per load, but only after the freshly-loaded nodes have been
    // measured (nodesInitialized) so we fit the new graph, not the old one.
    if (nodesInitialized && fittedKeyRef.current !== flowKey) {
      fittedKeyRef.current = flowKey;
      fitView({ padding: 0.2, duration: 300 });
    }
  }, [nodesInitialized, flowKey, fitView]);

  return null;
};

const FlowDropTarget = ({ onDrop, children }) => {
  const { screenToFlowPosition } = useReactFlow();
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/reactflow-template');
    if (!raw) return;
    const template = JSON.parse(raw);
    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    onDrop(template, position);
  };
  return (
    <Box onDragOver={handleDragOver} onDrop={handleDrop} sx={{ width: '100%', height: '100%' }}>
      {children}
    </Box>
  );
};

const connectionLineStyle = {
  stroke: dsVar('accent-fg'),
  strokeWidth: 2,
  strokeDasharray: '5,5'
};

const edgeOptions = {
  animated: true,
  style: {
    stroke: dsVar('accent-fg'),
    strokeWidth: 2,
  },
  type: 'smoothstep',
  markerEnd: {
    type: 'arrowclosed',
    color: dsVar('accent-fg'),
    width: 15,
    height: 15,
  },
};

const getLayoutedElements = (nodes, edges) => {
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'LR',
    align: 'UL',
    nodesep: 50,
    ranksep: 100,
    marginx: 50,
    marginy: 50
  });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((node) => g.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
  edges.forEach((edge) => g.setEdge(edge.source, edge.target));
  dagre.layout(g);
  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = g.node(node.id);
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
      return node;
    }),
    edges,
  };
};

const TaskFlowEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [text1, setText1] = useState('Untitled Task Flow');
  const [activitytemplates, setActivitytemplates] = useState();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowTemplateDetails, setFlowTemplateDetails] = useState();
  // Bumped on every successful flow load so the canvas re-fits on drill-down.
  const [flowLoadSeq, setFlowLoadSeq] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [isTestDrawerOpen, setIsTestDrawerOpen] = useState(false);
  const [inputFields, setInputFields] = useState([]);
  const [userInputs, setUserInputs] = useState({});
  const [showAPIDetails, setShowAPIDetails] = useState(false);
  const [executeAPIRes, setExecuteAPIRes] = useState();
  const [selectedNode, setSelectedNode] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [edgeMenuPosition, setEdgeMenuPosition] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editNodeDetails, setEditNodeDetails] = useState(null);
  const [editNodeConfig, setEditNodeConfig] = useState(null);
  const [isIntegrationDrawerOpen, setIsIntegrationDrawerOpen] = useState(false);
  const [isEdgeEditDrawerOpen, setIsEdgeEditDrawerOpen] = useState(false);
  const [editEdgeDetails, setEditEdgeDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerReadOnly, setIsDrawerReadOnly] = useState(true);
  const [isEdgeDrawerReadOnly, setIsEdgeDrawerReadOnly] = useState(true);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  // Hover preview state
  const [hoverPreview, setHoverPreview] = useState(null);
  const [previewPos, setPreviewPos] = useState(null);
  const previewRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  // Catalog panel view: 'assistant' (default) | 'list'
  const [catalogView, setCatalogView] = useState('assistant');
  useEffect(() => {
    const flowTemplateCode = location.state?.flow_template_code;
    if (flowTemplateCode) {
      getFlowTemplateDetails({ flow_template_code: flowTemplateCode });
    } else {
      getEditableActivityTemplateForOrg();
    }
  }, [location.state?.flow_template_code]);

  const getEditableActivityTemplateForOrg = async (scopeLevel) => {
    try {
      const res = await axios.get(`${environment.controlplaneUrl}/manage/activity-templates`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { activity_class: 'AGENT_TASK', ...(scopeLevel ? { scope_level: scopeLevel } : {}) },
        }
      );
      setActivitytemplates(res?.data?.activity_templates);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const getFlowTemplateDetails = async (rowData) => {
    try {
      const res = await axios.get(
        `${environment.controlplaneUrl}/manage/flow-templates/${encodeURIComponent(rowData.flow_template_code)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const flowTemplate = res.data?.flow_template;
      getEditableActivityTemplateForOrg(flowTemplate?.scope_level);
      const { configuration } = flowTemplate;
      setFlowTemplateDetails(flowTemplate);
      if (flowTemplate?.flow_template_name) setText1(flowTemplate.flow_template_name);
      const loadedNodes = configuration.nodes.map((node) => ({
        id: node.data.id,
        type: 'workflowNode',
        position: node.position || { x: 0, y: 0 },
        data: { ...node.data, label: node.data.activity_template_name || node.data.activityTemplateName },
      }));
      const loadedEdges = configuration.edges.map((edge) => ({
        id: `e${edge.data.source}-${edge.data.target}`,
        source: edge.data.source,
        target: edge.data.target,
        animated: true,
        type: 'smoothstep',
        style: { stroke: dsVar('accent-fg'), strokeWidth: 2.5, opacity: 0.6 },
        markerEnd: { type: 'arrowclosed', color: dsVar('accent-fg'), width: 18, height: 18 },
      }));
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(loadedNodes, loadedEdges);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
      setFlowLoadSeq((seq) => seq + 1);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const updateFlowTemplateDetails = async () => {
    try {
      const updatedNodes = nodes.map((node) => {
        const { label, ...dataWithoutLabel } = node.data;
        return { data: dataWithoutLabel };
      });
      const updatedEdges = edges.map((edge) => {
        const templateEdge = flowTemplateDetails?.configuration?.edges?.find(e => e.data?.source === edge.source && e.data?.target === edge.target);
        return {
          data: {
            source: edge.source,
            target: edge.target,
            ...(templateEdge?.data?.type && { type: templateEdge.data.type }),
            ...(templateEdge?.data?.properties && { properties: templateEdge.data.properties })
          }
        };
      });
      const payload = {
        flow_template_name: text1,
        flow_template_description: flowTemplateDetails?.flow_template_description || '',
        configuration: { nodes: updatedNodes, edges: updatedEdges },
      };
      await axiosInstance.put(
        `/flow-templates/${encodeURIComponent(flowTemplateDetails.flow_template_code)}`,
        payload
      );
      navigate(-1);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleExecuteFlow = async () => {
    try {
      const res = await axios.post(`${environment.controlplaneUrl}/agent/flow/execute`,
        { data: userInputs, user: { orgId: localStorage.getItem('org_id') }, agent: { code: flowTemplateDetails?.flow_template_code } },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (res.status === 200) setShowAPIDetails(true);
      setExecuteAPIRes(res.data);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleAddNode = (template) => {
    const activityType = template?.activity_type || template?.activityType || template?.activity_template_code;
    const activityCode = template?.activity_template_code || activityType;
    const activityName = template?.activity_template_name || template?.activityTemplateName;
    const activityDescription = template?.activity_template_description || template?.activityTemplateDescription;
    const newNode = {
      id: `${activityType}_${Date.now()}`,
      type: 'workflowNode',
      data: {
        id: `${activityType}_${Date.now()}`,
        activity_template_name: activityName,
        activity_type: activityType,
        activity_template_code: activityCode,
        activity_template_description: activityDescription,
        interaction_mode: template?.interaction_mode,
        scope: template?.scope,
      },
      position: { x: 100, y: 100 },
    };
    setNodes((nds) => [...nds, newNode]);
    setOpenDialog(false);
  };

  const onCatalogDragStart = (event, template) => {
    event.dataTransfer.setData('application/reactflow-template', JSON.stringify(template));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnCanvas = (template, position) => {
    const activityType = template?.activity_type || template?.activity_template_code;
    const activityCode = template?.activity_template_code || activityType;
    const newNode = {
      id: `${activityType}_${Date.now()}`,
      type: 'workflowNode',
      data: {
        id: `${activityType}_${Date.now()}`,
        activity_template_name: template?.activity_template_name,
        activity_type: activityType,
        activity_template_code: activityCode,
        activity_template_description: template?.activity_template_description,
        interaction_mode: template?.interaction_mode,
        scope: template?.scope,
      },
      position,
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const onConnect = useCallback(async (params) => {
    setEdges((eds) => addEdge(params, eds));

    const newEdgeTemplate = {
      data: {
        source: params.source,
        target: params.target,
        type: 'precedes',
        properties: []
      }
    };

    setFlowTemplateDetails(prev => {
      if (!prev || !prev.configuration) return prev;
      const edges = prev.configuration.edges || [];
      if (edges.find(e => e.data?.source === params.source && e.data?.target === params.target)) return prev;
      return { ...prev, configuration: { ...prev.configuration, edges: [...edges, newEdgeTemplate] } };
    });

    try {
      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [{
                  data: newEdgeTemplate.data
                }]
              }
            }
          ]
        }
      };
      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
      );
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  }, [setEdges, flowTemplateDetails?.flow_template_code, navigate]);

  const onNodeClick = (event, node) => {
    const linkedCode = node.data?.linked_flow_template_code;
    if (linkedCode && linkedCode !== '') {
      navigate('/AIStudio/customize', {
        state: {
          flow_template_code: linkedCode,
          // Back returns to this task flow rather than stepping through history.
          returnTo: { pathname: '/AIStudio/task-flow-editor', state: location.state },
        }
      });
    } else {
      handleEditNode(node, true);
    }
  };

  const onNodeMouseEnter = useCallback((event, node) => {
    clearTimeout(hoverTimerRef.current);
    clearTimeout(hideTimerRef.current);
    // Capture the node's rect synchronously — event.currentTarget is nulled
    // by React before the setTimeout below would read it.
    const nodeEl = event.currentTarget;
    hoverTimerRef.current = setTimeout(() => {
      const anchorRect = nodeEl?.getBoundingClientRect?.();
      // The description now ships with the flow-template node data, so read it
      // directly instead of round-tripping to /manage/activity-templates.
      setHoverPreview({
        anchorRect,
        data: {
          activity_template_name: node.data?.activity_template_name || node.data?.activityTemplateName,
          activity_template_description: node.data?.activity_template_description,
        },
        nodeRef: node,
      });
    }, 300);
  }, []);

  const onNodeMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setHoverPreview(null);
    }, 250);
  }, []);

  // Position the hover preview in the best available free space around the node:
  // prefer the right side, flip to the left if it would overflow, then clamp
  // into the viewport vertically. Uses the preview's measured size.
  useLayoutEffect(() => {
    if (!hoverPreview?.anchorRect || !previewRef.current) return;
    const rect = hoverPreview.anchorRect;
    const { offsetWidth: pw, offsetHeight: ph } = previewRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const GAP = 14;
    let left;
    if (rect.right + GAP + pw <= vw) left = rect.right + GAP;            // room on the right
    else if (rect.left - GAP - pw >= 0) left = rect.left - GAP - pw;     // flip to the left
    else left = Math.max(8, Math.min(vw - pw - 8, rect.right + GAP));    // clamp into view
    const top = Math.max(8, Math.min(rect.top - 10, vh - ph - 8));
    setPreviewPos({ top, left });
  }, [hoverPreview]);

  const onEdgeClick = (event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
    handleEditEdge(edge, true);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setMenuPosition(null);
  };

  const handleRemoveEdgeFromBackend = async (edgeSource, edgeTarget) => {
    setFlowTemplateDetails(prev => {
      if (!prev || !prev.configuration || !prev.configuration.edges) return prev;
      return {
        ...prev,
        configuration: {
          ...prev.configuration,
          edges: prev.configuration.edges.filter(e => !(e.data?.source === edgeSource && e.data?.target === edgeTarget))
        }
      };
    });

    try {
      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [{
                  data: {
                    source: edgeSource,
                    target: edgeTarget,
                    status: 'deleted'
                  }
                }]
              }
            }
          ]
        }
      };
      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
      );
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleDeleteEdge = async (edge) => {
    setEdges(eds => eds.filter(e => e.id !== edge.id));
    setEdgeMenuPosition(null);
    await handleRemoveEdgeFromBackend(edge.source, edge.target);
  };

  const onEdgesDelete = async (edgesToDelete) => {
    for (const edge of edgesToDelete) {
      await handleRemoveEdgeFromBackend(edge.source, edge.target);
    }
  };

  const handleEditEdge = (edge, readOnly = false) => {
    const templateEdge = flowTemplateDetails?.configuration?.edges?.find(e => e.data?.source === edge.source && e.data?.target === edge.target);
    const properties = templateEdge?.data?.properties || [];
    setIsEdgeDrawerReadOnly(readOnly);
    setEditEdgeDetails({
      source: edge.source,
      target: edge.target,
      properties: JSON.parse(JSON.stringify(properties))
    });

    setIsEdgeEditDrawerOpen(true);
  };

  const handleEditEdgePropertyChange = (propertyKey, newValue) => {
    setEditEdgeDetails((prev) => {
      if (!prev) return prev;
      const updatedProps = prev.properties.map(prop => {
        if ((prop.key || prop.name) === propertyKey) {
          return { ...prop, value: newValue };
        }
        return prop;
      });
      return { ...prev, properties: updatedProps };
    });
  };

  const handleSaveEditedEdge = async () => {
    try {
      const originalEdge = flowTemplateDetails?.configuration?.edges?.find(
        e => e.data?.source === editEdgeDetails.source && e.data?.target === editEdgeDetails.target
      );

      const payload = {
        data: {
          flow_template_items: [
            {
              flow_template_code: flowTemplateDetails?.flow_template_code,
              configuration: {
                edges: [
                  {
                    data: {
                      ...originalEdge?.data,
                      source: editEdgeDetails.source,
                      target: editEdgeDetails.target,
                      properties: editEdgeDetails.properties
                    }
                  }
                ]
              }
            }
          ]
        }
      };

      await axios.post(
        `${environment.apiUrl}/agent/updateCustomFlowControl`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setFlowTemplateDetails(prev => {
        if (!prev || !prev.configuration || !prev.configuration.edges) return prev;

        const updatedEdges = prev.configuration.edges.map(e => {
          if (e.data?.source === editEdgeDetails.source && e.data?.target === editEdgeDetails.target) {
            return {
              ...e,
              data: {
                ...e.data,
                properties: editEdgeDetails.properties
              }
            };
          }
          return e;
        });

        return {
          ...prev,
          configuration: {
            ...prev.configuration,
            edges: updatedEdges
          }
        };
      });
      setIsEdgeEditDrawerOpen(false);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleEditNode = async (node, readOnly = false) => {
    try {
      setMenuPosition(null);
      setIsDrawerReadOnly(readOnly);
      let activityCode = node.data?.activity_template_code || node.id || null;
      if (activityCode && activityCode.includes('_') && /_\d{10,}$/.test(activityCode)) {
        activityCode = activityCode.substring(0, activityCode.lastIndexOf('_'));
      }
      const res = await axios.get(
        `${environment.controlplaneUrl}/manage/activity-templates/${encodeURIComponent(activityCode)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const responseData = res.data?.activity_template || res.data?.data || res.data;
      const config = responseData?.configuration || responseData;
      setEditNodeDetails({
        id: responseData?.id || null,
        activity_type: responseData?.activity_type || node.data?.activity_type || '',
        activity_template_name: responseData?.activity_template_name || '',
        activity_template_description: responseData?.activity_template_description || '',
        activity_template_code: activityCode,
        primary_input: config?.primary_input || [],
        primary_output: config?.primary_output || null,
        // Data source binding — the same response already carries these, so the
        // drawer surfaces the binding without an extra round trip. Sub-flow
        // nodes never show it, even via the context-menu edit path.
        has_integration_binding:
          responseData?.has_integration_binding === true && !node.data?.linked_flow_template_code,
        integration_binding_id: responseData?.integration_binding_id || null,
        integration_alias: responseData?.integration_alias || null,
        integration_object: responseData?.object || null,
      });
      setEditNodeConfig(config?.task_configuration || config?.taskConfiguration || config?.primaryInput || config?.task_configuration_items || []);
      setIsDescExpanded(false);
      setIsIntegrationDrawerOpen(false);
      setIsEditDrawerOpen(true);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const handleEditNodeConfigChange = (key, value) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) => (item.key === key ? { ...item, value } : item))
    );

  const handleEditNodeCellChange = (key, rowIndex, colKey, value) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) =>
        item.key !== key || !Array.isArray(item.value)
          ? item
          : {
              ...item,
              value: item.value.map((row, i) =>
                i === rowIndex ? { ...row, [colKey]: value } : row
              ),
            }
      )
    );

  // Soft delete/restore. The element or row stays in the array with active:false
  // so it round-trips into the PUT payload (task_configuration is sent verbatim)
  // and stays restorable.
  const handleEditNodeFieldActiveChange = (key, active) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) => (item.key === key ? { ...item, active } : item))
    );

  const handleEditNodeRowActiveChange = (key, rowIndex, active) =>
    setEditNodeConfig((prev) =>
      (prev || []).map((item) =>
        item.key !== key || !Array.isArray(item.value)
          ? item
          : {
              ...item,
              value: item.value.map((row, i) => (i === rowIndex ? { ...row, active } : row)),
            }
      )
    );

  const handleSaveEditedNode = async () => {
    try {
      const activityCode = editNodeDetails?.activity_template_code;
      const payload = {
        activity_template_name: editNodeDetails?.activity_template_name || '',
        activity_template_description: editNodeDetails?.activity_template_description || '',
        configuration: {
          primary_input: editNodeDetails?.primary_input || [],
          primary_output: editNodeDetails?.primary_output || null,
          task_configuration: editNodeConfig,
        },
      };
      await axios.put(
        `${environment.controlplaneUrl}/manage/activity-templates/${encodeURIComponent(activityCode)}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setIsEditDrawerOpen(false);
    } catch (error) {
      if (['jwt malformed', 'jwt expired'].includes(error?.response?.data?.error)) navigate('/login');
    }
  };

  const filteredTemplates = activitytemplates?.filter(template => {
    const name = (template.activity_template_name || template.activityTemplateName || '').toLowerCase();
    const description = (template.activity_template_description || template.activityTemplateDescription || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || description.includes(term);
  });

  const groupedTemplates = filteredTemplates?.reduce((acc, template) => {
    const rawType = template.task_type || template.taskType || template.activity_type || template.activityType || 'Uncategorized';
    const taskType = toTitleCase(rawType);
    if (!acc[taskType]) acc[taskType] = [];
    acc[taskType].push(template);
    return acc;
  }, {});

  return (
    <Box sx={{ backgroundColor: dsVar('page'), height: '100vh', color: dsVar('fg-body'), display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 24px 16px', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: ink('#159DFF') }}><KeyboardBackspaceIcon /></IconButton>
          <Typography sx={{ color: dsVar('accent-fg'), fontWeight: 600, fontSize: '18px' }}>Configure Agent Tasks</Typography>
          <TextField
            size="small"
            value={text1}
            label="Give your Agent a Name"
            variant="standard"
            sx={{
              width: '300px',
              '& .MuiInput-root': { color: dsVar('fg-body'), fontSize: '18px' },
              '& .MuiInputLabel-root': { color: dsVar('fg-2') }
            }}
            onChange={(e) => setText1(e.target.value)}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <ButtonK text="Save" color="#131F32" backgroundColor="#FFD600" handleClick={updateFlowTemplateDetails} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', borderTop: `1px solid ${dsVar('line')}`, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* ── Left Panel: Task Catalog ── */}
        <Box sx={{ width: '28%', borderRight: `1px solid ${dsVar('line')}`, display: 'flex', flexDirection: 'column', backgroundColor: dsVar('surface') }}>

          {/* Toggle header */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: `1px solid ${dsVar('line')}` }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: dsVar('fg-muted'), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>Task Catalog</Typography>
            <Box sx={{ display: 'flex', gap: '4px', backgroundColor: dsVar('page'), borderRadius: '10px', p: '4px' }}>
              <Box
                onClick={() => setCatalogView('assistant')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  py: '6px', borderRadius: '7px', cursor: 'pointer', transition: 'all 0.2s ease',
                  backgroundColor: catalogView === 'assistant' ? '#0F2D1F' : 'transparent',
                  border: catalogView === 'assistant' ? '1px solid #10B981' : '1px solid transparent',
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 13, color: catalogView === 'assistant' ? ink('#10B981') : dsVar('fg-muted') }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: catalogView === 'assistant' ? ink('#10B981') : dsVar('fg-muted') }}>Assistant</Typography>
              </Box>
              <Box
                onClick={() => setCatalogView('list')}
                sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  py: '6px', borderRadius: '7px', cursor: 'pointer', transition: 'all 0.2s ease',
                  backgroundColor: catalogView === 'list' ? dsVar('raised') : 'transparent',
                  border: catalogView === 'list' ? `1px solid ${dsVar('line')}` : '1px solid transparent',
                }}
              >
                <ViewListIcon sx={{ fontSize: 13, color: catalogView === 'list' ? dsVar('fg-body') : dsVar('fg-muted') }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: catalogView === 'list' ? dsVar('fg-body') : dsVar('fg-muted') }}>Catalog</Typography>
              </Box>
            </Box>
          </Box>

          {/* ── View: Agentic Workflow Architect ── */}
          <Box sx={{ flex: 1, display: catalogView === 'assistant' ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
            <ChatAssistance
              agentCode="QUERY_AGENT_ORCHESTRATION_AND_RESPOND"
              title="Agentic Workflow Architect"
              subtitle="AI Powered • Online"
              initialMessage="Hi! I'm your Agentic Workflow Architect. Describe the tasks you need and I'll help you design the task flow — breaking down logic, sequencing steps, and wiring dependencies."
            />
          </Box>

          {/* ── View: Task Catalog List ── */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: catalogView === 'list' ? 'block' : 'none' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  marginBottom: '12px',
                  '& .MuiOutlinedInput-root': {
                    color: dsVar('fg-body'),
                    backgroundColor: dsVar('page'),
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  },
                  '& .MuiInputBase-input::placeholder': { color: dsVar('fg-2'), opacity: 1 }
                }}
              />
              <ButtonK text="+ Create an AI Task" color="#131F32" backgroundColor="#FFD600" sx={{ width: '100%', marginBottom: '12px' }} handleClick={() => setOpenDialog(true)} />
              {Object.entries(groupedTemplates || {}).map(([taskType, templates]) => (
                <Accordion key={taskType} sx={{ backgroundColor: dsVar('surface'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`, marginBottom: '8px' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dsVar('fg-2') }} />}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{taskType} ({templates.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ overflow: 'visible' }}>
                    {templates.map((t, i) => (
                      <Card
                        key={i}
                        draggable
                        onDragStart={(e) => onCatalogDragStart(e, t)}
                        sx={{
                          backgroundColor: dsVar('raised'),
                          borderRadius: '4px',
                          border: `1px solid ${dsVar('line')}`,
                          borderLeft: '4px solid #10B981',
                          marginBottom: '8px',
                          position: 'relative',
                          cursor: 'grab',
                          transition: 'all 0.2s ease',
                          overflow: 'visible',
                          '&:hover': {
                            borderColor: '#10B981',
                            boxShadow: '0 0 10px rgba(16, 185, 129, 0.1)'
                          }
                        }}>
                        <CardContent sx={{ padding: '12px', position: 'relative', display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '8px' }}>
                          <ModeLabelChip mode={t.interaction_mode} />
                          <ScopeBadge scope={t.scope} />
                          <Box sx={{ color: ink('#159DFF'), flexShrink: 0, display: 'flex', alignItems: 'center', mt: '2px', mr: '2px' }}>
                            <DirectionsRunIcon sx={{ fontSize: '18px' }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: dsVar('fg-body'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.activity_template_name || t.activityTemplateName || 'Unnamed Task'}
                            </Typography>
                            {t.org_id && (
                              <Chip
                                label="CUSTOM"
                                size="small"
                                sx={{
                                  height: '16px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em',
                                  mt: '4px', mb: '2px',
                                  backgroundColor: 'rgba(167,139,250,0.08)',
                                  color: ink('#A78BFA'),
                                  border: '1px solid rgba(167,139,250,0.25)',
                                  borderRadius: '4px',
                                  '& .MuiChip-label': { px: '6px' },
                                }}
                              />
                            )}
                            <Typography sx={{ fontSize: '11px', color: dsVar('fg-2'), marginTop: '4px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {t.activity_template_description || t.activityTemplateDescription || ''}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
        </Box>

        {/* ── Right Panel: ReactFlow Canvas ── */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          backgroundColor: dsVar('page'),
          '& .react-flow__panel.react-flow__minimap': {
            margin: '0px !important',
            top: 'auto !important',
            left: 'auto !important',
            right: '0px !important',
            bottom: '0px !important',
            borderBottomRightRadius: '0 !important',
            borderTopRightRadius: '0 !important',
            borderBottomLeftRadius: '0 !important',
            position: 'absolute !important',
            transform: 'none !important',
          }
        }}>
          <style>{nodeStyles}</style>
          <ReactFlowProvider>
            <FlowDropTarget onDrop={handleDropOnCanvas}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onNodeMouseEnter={onNodeMouseEnter}
              onNodeMouseLeave={onNodeMouseLeave}
              onEdgeClick={onEdgeClick}
              onEdgesDelete={onEdgesDelete}
              nodeTypes={nodeTypes}
              connectionLineStyle={connectionLineStyle}
              defaultEdgeOptions={edgeOptions}
              fitView
            >
              <FitViewOnFlowChange flowKey={flowLoadSeq} />
              <Controls />
              <MiniMap
                className="custom-minimap"
                position="bottom-right"
                ariaLabel="Task Flow Mini Map"
                nodeColor={(node) => {
                  const isSubFlow = node.data?.linked_flow_template_code && node.data.linked_flow_template_code !== '';
                  if (isSubFlow) return '#7C3AED';
                  const c = taskTypeConfig[node.data?.activity_type] || taskTypeConfig[node.data?.activityType];
                  return c?.color || '#607D8B';
                }}
                maskColor={dsVar('page', 0.85)}
                style={{ backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, borderRadius: '8px', margin: 0, right: 0, bottom: 0 }}
                title="Task Flow Mini Map"
              />
              <Background color={dsVar('line')} variant="dots" gap={20} size={1} />
            </ReactFlow>
            </FlowDropTarget>
          </ReactFlowProvider>
          {/* Hover preview popover */}
          {hoverPreview && (
            <Box
              ref={previewRef}
              onMouseEnter={() => clearTimeout(hideTimerRef.current)}
              onMouseLeave={onNodeMouseLeave}
              sx={{
                position: 'fixed',
                zIndex: 1500,
                top: previewPos ? previewPos.top : (hoverPreview.anchorRect?.top || 200) - 10,
                left: previewPos ? previewPos.left : (hoverPreview.anchorRect?.right || 400) + 14,
                visibility: previewPos ? 'visible' : 'hidden',
                maxWidth: 320,
                minWidth: 220,
                backgroundColor: dsVar('raised'),
                border: `1px solid ${dsVar('line')}`,
                borderRadius: '12px',
                padding: '16px',
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${dsVar('fg', 0.04)}`,
                backdropFilter: 'blur(8px)',
                animation: 'fadeInPreview 0.15s ease-out',
                '@keyframes fadeInPreview': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: dsVar('fg'), mb: 0.75, fontFamily: "'JetBrains Mono', monospace" }}>
                {hoverPreview.data?.activity_template_name || 'Task'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: dsVar('fg-2'), lineHeight: 1.6 }}>
                {(hoverPreview.data?.activity_template_description || '').length > 150
                  ? `${hoverPreview.data.activity_template_description.substring(0, 150)}\u2026`
                  : hoverPreview.data?.activity_template_description || 'No description available.'}
              </Typography>
              <Typography
                onClick={() => { setHoverPreview(null); handleEditNode(hoverPreview.nodeRef, true); }}
                sx={{
                  fontSize: '11px',
                  color: ink('#10B981'),
                  mt: 1.5,
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  '&:hover': { textDecoration: 'underline', color: ink('#34D399') },
                  transition: 'color 0.15s ease',
                }}
              >
                View details &rarr;
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: dsVar('page'), color: dsVar('accent-fg') }}>Create an AI Task</DialogTitle>
        <DialogContent sx={{ backgroundColor: dsVar('page') }}>
          <AdminActivityTemplates />
        </DialogContent>
      </Dialog>

      <Drawer anchor="right" open={isTestDrawerOpen} onClose={() => setIsTestDrawerOpen(false)} PaperProps={{ sx: { width: '500px', backgroundColor: dsVar('surface'), color: dsVar('fg-body') } }}>
        <Box sx={{ padding: '24px' }}>
          <Typography variant="h6" sx={{ color: dsVar('accent-fg'), marginBottom: '24px' }}>Test Your AI Agent</Typography>
          {inputFields?.map((f) => (
            <Box key={f.key} sx={{ marginBottom: '16px' }}>
              <Typography sx={{ marginBottom: '8px' }}>{f.label}</Typography>
              {f.type === 'TEXTINPUT' && <TextField fullWidth multiline rows={3} value={userInputs[f.key] || ''} onChange={(e) => setUserInputs({ ...userInputs, [f.key]: e.target.value })} sx={{ backgroundColor: dsVar('surface'), input: { color: dsVar('fg-body') } }} />}
            </Box>
          ))}
          <ButtonK text="Execute Agent" backgroundColor="#FFDE59" sx={{ width: '100%', marginTop: '24px' }} handleClick={handleExecuteFlow} />
          {showAPIDetails && <pre sx={{ marginTop: '24px', backgroundColor: dsVar('page'), padding: '16px' }}>{JSON.stringify(executeAPIRes, null, 2)}</pre>}
        </Box>
      </Drawer>

      {/* Node context menu removed — drawer opens directly on node click */}

      {/* Edge context menu removed — drawer opens directly on edge click */}

      <Drawer anchor="right" open={isEditDrawerOpen} onClose={() => setIsEditDrawerOpen(false)} PaperProps={{ sx: { width: '500px', backgroundColor: dsVar('surface'), color: dsVar('fg-body') } }}>
        <Box sx={{ padding: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>Edit Task Parameters</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                title={isDrawerReadOnly ? 'Switch to Edit mode' : 'Switch to Read-only mode'}
                onClick={() => setIsDrawerReadOnly(r => !r)}
                sx={{
                  color: isDrawerReadOnly ? dsVar('fg-2') : ink('#10B981'),
                  backgroundColor: isDrawerReadOnly ? dsVar('raised') : 'rgba(16, 185, 129, 0.1)',
                  border: `1px solid ${isDrawerReadOnly ? dsVar('line') : '#10B981'}`,
                  borderRadius: '8px',
                  px: 1.5,
                  gap: 0.5,
                  '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.15)' }
                }}
              >
                {isDrawerReadOnly ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>
                  {isDrawerReadOnly ? 'Read-only' : 'Editing'}
                </Typography>
              </IconButton>
              <IconButton onClick={() => setIsEditDrawerOpen(false)} sx={{ color: dsVar('fg-2') }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1 }}>Task Name</Typography>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              disabled={isDrawerReadOnly}
              value={editNodeDetails?.activity_template_name || ''}
              onChange={(e) => setEditNodeDetails({ ...editNodeDetails, activity_template_name: e.target.value })}
              sx={{
                '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '15px', backgroundColor: dsVar('surface') },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
              }}
            />
            <Box sx={{ mt: 2.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }}>Task Description</Typography>
              {(editNodeDetails?.activity_template_description || '').length > 120 && (
                <Box
                  onClick={() => setIsDescExpanded((v) => !v)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', color: ink('#159DFF'), fontSize: '12px', fontWeight: 600, userSelect: 'none' }}
                >
                  {isDescExpanded ? 'Show less' : 'Show more'}
                  <ExpandMoreIcon sx={{ fontSize: 16, transform: isDescExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </Box>
              )}
            </Box>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              maxRows={isDescExpanded ? 20 : 2}
              variant="outlined"
              disabled={isDrawerReadOnly}
              value={editNodeDetails?.activity_template_description || ''}
              onChange={(e) => setEditNodeDetails({ ...editNodeDetails, activity_template_description: e.target.value })}
              sx={{
                '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
              }}
            />
          </Box>

          {editNodeDetails?.has_integration_binding && editNodeDetails?.integration_alias && (
            <IntegrationBindingCard
              alias={editNodeDetails.integration_alias}
              accent="#10B981"
              onOpen={() => setIsIntegrationDrawerOpen(true)}
            />
          )}

          <TaskConfigurationForm
            key={editNodeDetails?.activity_template_code || 'node-config'}
            config={editNodeConfig}
            readOnly={isDrawerReadOnly}
            accent="#10B981"
            onFieldChange={handleEditNodeConfigChange}
            onCellChange={handleEditNodeCellChange}
            onFieldActiveChange={handleEditNodeFieldActiveChange}
            onRowActiveChange={handleEditNodeRowActiveChange}
          />
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${dsVar('line')}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedNode && (
              <IconButton
                size="small"
                title="Delete node"
                onClick={() => { handleDeleteNode(selectedNode.id); setIsEditDrawerOpen(false); }}
                sx={{ color: ink('#FF4D4D'), border: '1px solid #FF4D4D', borderRadius: '8px', px: 1.5, '&:hover': { backgroundColor: 'rgba(255,77,77,0.1)' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>Delete</Typography>
              </IconButton>
            )}
            {!isDrawerReadOnly && (
              <ButtonK text="Save" backgroundColor="#FFDE59" color="#131F32" sx={{ fontWeight: 600, px: 4 }} handleClick={handleSaveEditedNode} />
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer anchor="right" open={isEdgeEditDrawerOpen} onClose={() => setIsEdgeEditDrawerOpen(false)} PaperProps={{ sx: { width: '500px', backgroundColor: dsVar('surface'), color: dsVar('fg-body') } }}>
        <Box sx={{ padding: '24px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: dsVar('accent-fg'), fontWeight: 600 }}>Control Flow Parameters</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                title={isEdgeDrawerReadOnly ? 'Switch to Edit mode' : 'Switch to Read-only mode'}
                onClick={() => setIsEdgeDrawerReadOnly(r => !r)}
                sx={{
                  color: isEdgeDrawerReadOnly ? dsVar('fg-2') : ink('#10B981'),
                  backgroundColor: isEdgeDrawerReadOnly ? dsVar('raised') : 'rgba(16, 185, 129, 0.1)',
                  border: `1px solid ${isEdgeDrawerReadOnly ? dsVar('line') : '#10B981'}`,
                  borderRadius: '8px',
                  px: 1.5,
                  gap: 0.5,
                  '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.15)' }
                }}
              >
                {isEdgeDrawerReadOnly ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>
                  {isEdgeDrawerReadOnly ? 'Read-only' : 'Editing'}
                </Typography>
              </IconButton>
              <IconButton onClick={() => setIsEdgeEditDrawerOpen(false)} sx={{ color: dsVar('fg-2') }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>Source Node</Typography>
            <Typography sx={{ color: dsVar('fg-body'), fontSize: '14px', mb: 2 }}>
              {nodes.find(n => n.id === editEdgeDetails?.source)?.data?.activity_template_name || editEdgeDetails?.source || 'N/A'}
            </Typography>

            <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 1 }}>Target Node</Typography>
            <Typography sx={{ color: dsVar('fg-body'), fontSize: '14px', mb: 2 }}>
              {nodes.find(n => n.id === editEdgeDetails?.target)?.data?.activity_template_name || editEdgeDetails?.target || 'N/A'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {editEdgeDetails?.properties?.length === 0 && (
              <Typography sx={{ color: dsVar('fg-2'), fontSize: '14px', fontStyle: 'italic' }}>No configurable parameters for this edge.</Typography>
            )}
            {editEdgeDetails?.properties?.map((prop, idx) => {
              const { type, label, info, key, name, value, options } = prop;
              const propKey = key || name || String(idx);
              const propLabel = label || propKey;

              if (type === 'MULTITEXT') {
                return (
                  <Box key={propKey} sx={{ mb: 3 }}>
                    <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                    <Box sx={{
                      '& .MuiInputBase-root': { color: dsVar('fg-body'), backgroundColor: dsVar('surface'), borderRadius: '8px', fontSize: '14px' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                      '& .MuiChip-root': { backgroundColor: dsVar('raised'), color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}` },
                      '& .MuiChip-deleteIcon': { color: dsVar('fg-2'), '&:hover': { color: ink('#FF4D4D') } },
                      '& .MuiTypography-root': { display: 'none' },
                    }}>
                      <ChipInput
                        question=""
                        minRows={3}
                        placeholder={info || propLabel}
                        values={value || []}
                        onChipChange={(chips) => handleEditEdgePropertyChange(propKey, chips)}
                      />
                    </Box>
                  </Box>
                );
              } else if (type === 'TEXTINPUT') {
                if (options && options.length > 0) {
                  const currentValueObj = typeof value === 'object' && value !== null ? (value.id !== undefined ? value.id : value.value !== undefined ? value.value : JSON.stringify(value)) : (value || '');
                  return (
                    <Box key={propKey} sx={{ mb: 3 }}>
                      <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={isEdgeDrawerReadOnly}
                        value={currentValueObj}
                        onChange={(e) => {
                          const selectedPrimitive = e.target.value;
                          const selectedOpt = options.find(opt => {
                            const isObj = typeof opt === 'object' && opt !== null;
                            const optVal = isObj ? (opt.id !== undefined ? opt.id : opt.value !== undefined ? opt.value : JSON.stringify(opt)) : opt;
                            return optVal === selectedPrimitive;
                          });
                          handleEditEdgePropertyChange(propKey, selectedOpt !== undefined ? selectedOpt : selectedPrimitive);
                        }}
                        sx={{
                          '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '& .MuiSvgIcon-root': { color: dsVar('fg-2') },
                          textAlign: 'left'
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: { backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, color: dsVar('fg-body') }
                            }
                          }
                        }}
                      >
                         {options.map((opt, optIdx) => {
                           const isObj = typeof opt === 'object' && opt !== null;
                           const optValue = isObj ? (opt.id !== undefined ? opt.id : opt.value !== undefined ? opt.value : JSON.stringify(opt)) : opt;
                           const optLabel = isObj ? (opt.label !== undefined ? opt.label : opt.name !== undefined ? opt.name : JSON.stringify(opt)) : opt;
                           return (
                             <MenuItem key={optIdx} value={optValue} sx={{ color: dsVar('fg-body'), '&:hover': { backgroundColor: dsVar('raised') }, '&.Mui-selected': { backgroundColor: dsVar('line') } }}>{optLabel}</MenuItem>
                           );
                         })}
                      </TextField>
                    </Box>
                  );
                } else {
                  const currentValuePlain = typeof value === 'object' && value !== null ? (value.id !== undefined ? value.id : value.value !== undefined ? value.value : JSON.stringify(value)) : (value || '');
                  return (
                    <Box key={propKey} sx={{ mb: 3 }}>
                      <Typography sx={{ color: dsVar('fg-2'), fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em', mb: 1.5 }}>{propLabel}</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        variant="outlined"
                        disabled={isEdgeDrawerReadOnly}
                        value={currentValuePlain}
                        onChange={(e) => handleEditEdgePropertyChange(propKey, e.target.value)}
                        sx={{
                          '& .MuiInputBase-root': { color: dsVar('fg-body'), fontSize: '14px', backgroundColor: dsVar('surface') },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: dsVar('line') },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#159DFF' }
                        }}
                      />
                    </Box>
                  );
                }
              }
              return null;
            })}
          </Box>
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${dsVar('line')}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedEdge && (
              <IconButton
                size="small"
                title="Delete edge"
                onClick={() => { handleDeleteEdge(selectedEdge); setIsEdgeEditDrawerOpen(false); }}
                sx={{ color: ink('#FF4D4D'), border: '1px solid #FF4D4D', borderRadius: '8px', px: 1.5, '&:hover': { backgroundColor: 'rgba(255,77,77,0.1)' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, ml: 0.5 }}>Delete</Typography>
              </IconButton>
            )}
            {!isEdgeDrawerReadOnly && (
              <ButtonK text="Save" backgroundColor="#FFDE59" color="#131F32" sx={{ fontWeight: 600, px: 4 }} handleClick={handleSaveEditedEdge} />
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Mounted last so it layers over the node drawer, which stays open underneath. */}
      <IntegrationExplorerDrawer
        open={isIntegrationDrawerOpen}
        onClose={() => setIsIntegrationDrawerOpen(false)}
        integrationAlias={editNodeDetails?.integration_alias}
        defaultObjectName={editNodeDetails?.integration_object}
        accent="#10B981"
      />
    </Box>
  );
};

export default TaskFlowEditor;
